/**
 * @file Controlled-drug subview route helpers.
 */

const DRUG_ROUTE_SEGMENTS = Object.freeze({
  actions: "actions",
  stock: "stock",
  history: "history",
  audit: "audit",
  auditHistory: "audit",
  dashboard: "stock",
});

/**
 * Normalises a drugs subview value to its canonical route segment.
 * @param {string|null} subView Drugs subview value.
 * @returns {string|null} Canonical drugs subview, or null for the default route.
 */
export function normalizeDrugSubView(subView) {
  const rawValue = String(subView || "").trim();
  if (!rawValue) {return null;}
  return DRUG_ROUTE_SEGMENTS[rawValue] || null;
}

/**
 * Builds a canonical drugs path.
 * @param {string|null} subView Optional drugs subview.
 * @returns {string} Canonical drugs path.
 */
export function buildDrugPath(subView = null) {
  const normalizedSubView = normalizeDrugSubView(subView);
  return normalizedSubView ? `/drugs/${normalizedSubView}` : "/drugs";
}

/**
 * Parses a canonical drugs path.
 * @param {string} pathname URL pathname.
 * @returns {object|null} Parsed drugs route data or null.
 */
export function parseDrugPath(pathname) {
  if (!pathname) {return null;}

  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "drugs") {return null;}

  if (segments.length === 1) {
    return {
      view: "drugs",
      subView: null,
      pathname: "/drugs",
    };
  }

  if (segments.length === 2) {
    const subView = normalizeDrugSubView(segments[1]);
    if (!subView) {return null;}
    return {
      view: "drugs",
      subView,
      pathname: buildDrugPath(subView),
    };
  }

  return null;
}

/**
 * Returns whether the pathname is in the drugs route namespace.
 * @param {string} pathname URL pathname.
 * @returns {boolean}
 */
export function isDrugLikePath(pathname) {
  if (!pathname) {return false;}
  return pathname.split("/").filter(Boolean)[0] === "drugs";
}

/**
 * Parses a pathname into a drugs route candidate.
 * @param {string} pathname URL pathname.
 * @returns {{status: "valid", target: object}|{status: "invalid"}|null}
 */
export function parseDrugRouteCandidate(pathname) {
  const target = parseDrugPath(pathname);
  if (target) {
    return { status: "valid", target };
  }

  if (isDrugLikePath(pathname)) {
    return { status: "invalid" };
  }

  return null;
}
